import React, { useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  GraduationCap, 
  Shield, 
  Rocket, 
  Users, 
  Heart, 
  ChevronRight, 
  Star,
  Building2,
  Briefcase,
  BookOpen,
  Target,
  Sparkles,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';

import ParticleBackground from '../components/ui/ParticleBackground';

const UnityScholarsProgram = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 0.95]);

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.25, 0.1, 0.25, 1] } }
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } }
  };

  const pillars = [
    {
      icon: BookOpen,
      title: "Academic Excellence",
      description: "Rigorous STEM curriculum with personalized mentorship, focusing on cybersecurity, AI, and emerging technologies."
    },
    {
      icon: Briefcase,
      title: "Real-World Experience",
      description: "Paid work-study positions gaining hands-on experience with cutting-edge defense and technology projects."
    },
    {
      icon: Shield,
      title: "Security Clearance Pathway",
      description: "Structured preparation for federal security clearances, opening doors to careers in national security and intelligence."
    },
    {
      icon: Heart,
      title: "Holistic Support",
      description: "Comprehensive wraparound services including housing assistance, transportation, and mental health resources."
    }
  ];

  const timeline = [
    { phase: "Application", duration: "Spring", description: "Open enrollment for rising high school juniors and seniors in foster care" },
    { phase: "Selection", duration: "Early Summer", description: "Interviews, assessments, and cohort selection" },
    { phase: "Onboarding", duration: "Summer", description: "Intensive orientation, security protocols, and team building" },
    { phase: "Work-Study", duration: "Academic Year", description: "Part-time work alongside coursework with dedicated mentors" },
    { phase: "Advancement", duration: "Ongoing", description: "College placement support and federal career pathways" }
  ];

  const goals = [
    { metric: "Paid", label: "Work-Study Positions" },
    { metric: "Direct", label: "Federal Pathways" }
  ];

  return (
    <div className="min-h-screen bg-black text-gray-100 overflow-hidden">
      {/* Concept proposal banner */}
      <div className="relative z-30 bg-gray-900 border-b border-gray-700/50 py-3 px-4 text-center mt-24">
        <p className="text-sm text-gray-300">
          This is a concept proposal. The program does not yet exist. We are collecting
          expressions of interest to support our planning.
        </p>
      </div>

      {/* Hero Section */}
      <motion.section 
        className="relative min-h-screen flex items-center justify-center"
        style={{ opacity: heroOpacity, scale: heroScale }}
      >
        <ParticleBackground />
        
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/20 via-transparent to-gray-900/20 z-10" />
        
        {/* Animated grid */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }} />
        </div>

        {/* Floating orbs */}
        <motion.div 
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-gray-600/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 50, 0], 
            y: [0, -30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gray-500/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -40, 0], 
            y: [0, 40, 0],
            scale: [1, 1.15, 1]
          }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="container mx-auto px-4 z-20 relative">
          <motion.div 
            className="max-w-5xl mx-auto text-center"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {/* Badge */}
            <motion.div variants={fadeUp} className="mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-600/50 bg-gray-800/30 text-gray-300 text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Program in Development
              </span>
            </motion.div>

            {/* Main heading */}
            <motion.h1 
              variants={fadeUp}
              className="text-5xl md:text-7xl lg:text-8xl font-extralight tracking-tight text-white mb-6"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Unity <span className="text-gray-400">Scholars</span> Program
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="text-xl md:text-2xl text-gray-300 font-light mb-4 max-w-3xl mx-auto"
            >
              Transforming foster youth into the next generation of national security leaders
            </motion.p>

            <motion.p 
              variants={fadeUp}
              className="text-gray-500 mb-12 max-w-2xl mx-auto"
            >
              A proposed high school work-study program creating direct pathways from foster care to federal careers in cybersecurity, AI, and defense technology.
            </motion.p>

            {/* Scroll indicator */}
            <motion.div 
              variants={fadeUp}
              className="flex justify-center mb-10"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-6 h-10 rounded-full border-2 border-gray-600 flex items-start justify-center p-2">
                <motion.div 
                  className="w-1.5 h-1.5 bg-gray-400 rounded-full"
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
            </motion.div>

            {/* CTA buttons */}
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-black/30 border border-gray-600/30"
              >
                Express Interest
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#program-details"
                className="inline-flex items-center justify-center px-8 py-4 border border-gray-700 hover:border-gray-500 text-gray-300 hover:text-white font-medium rounded-lg transition-all duration-300"
              >
                Learn More
              </a>
            </motion.div>

          </motion.div>
        </div>
      </motion.section>
{/* Mission Statement */}
<section className="relative py-24 bg-gradient-to-b from-black to-gray-950">
  <div className="container mx-auto px-4">
    <motion.div 
      className="max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
    >
      <div className="relative">
        <div className="absolute -top-8 -left-4 text-8xl text-gray-700/30 font-serif">"</div>
        
        <blockquote className="relative z-10 text-lg md:text-xl text-gray-300 font-light leading-relaxed pl-8 border-l-2 border-gray-700">
          <p className="mb-6">
            Every young person deserves more than survival—they deserve a pathway to excellence. Our mission is to open doors to the highest levels of national service, regardless of circumstance.
          </p>
          <p className="text-gray-400">
            Your background is not a limitation. It's the foundation of your resilience.
          </p>
        </blockquote>

        <div className="mt-8 pl-8">
          <p className="text-white font-medium">— Unity Scholars</p>
          <p className="text-gray-500 text-sm">A program built on belief in your potential</p>
        </div>
      </div>
    </motion.div>
  </div>
</section>

      {/* Goals Grid */}
      <section className="relative py-16 bg-gray-950 border-y border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.p
            className="text-center text-gray-600 text-sm uppercase tracking-wider mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Program Goals
          </motion.p>
          <motion.p
            className="text-center text-gray-500 text-sm max-w-xl mx-auto mb-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Aspirational metrics to be defined if the program launches.
          </motion.p>
          <div className="max-w-2xl mx-auto grid grid-cols-2 gap-8">
            {goals.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl md:text-5xl font-light text-white mb-2">
                  {item.metric}
                </p>
                <p className="text-gray-500 text-sm uppercase tracking-wider">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Pillars */}
      <section id="program-details" className="relative py-24 bg-gray-950">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-4 tracking-wide"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Program Pillars
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              A comprehensive approach to developing tomorrow's national security workforce
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {pillars.map((pillar, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/20 to-gray-700/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative bg-gray-900/60 border border-gray-800/50 rounded-xl p-8 hover:border-gray-700/50 transition-all duration-300">
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0 w-14 h-14 rounded-lg bg-gray-800/50 border border-gray-700/50 flex items-center justify-center group-hover:border-gray-600/50 transition-colors">
                      <pillar.icon className="w-7 h-7 text-gray-400 group-hover:text-gray-300 transition-colors" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-white mb-2">{pillar.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{pillar.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 bg-black">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-4 tracking-wide"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Proposed Scholar Journey
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              From application to federal career placement
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-gray-600/50 via-gray-700/50 to-transparent" />

              {timeline.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`relative flex items-center gap-8 mb-12 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-20 md:pl-0`}>
                    <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">{item.duration}</span>
                    <h3 className="text-xl font-medium text-white mt-1 mb-2">{item.phase}</h3>
                    <p className="text-gray-500 text-sm">{item.description}</p>
                  </div>

                  {/* Node */}
                  <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gray-900 border-2 border-gray-600 z-10" />

                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block flex-1" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="relative py-20 bg-gray-950 border-y border-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl font-light text-white mb-2 tracking-wide"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Our Vision
            </h2>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto">
              Building pathways to federal careers in national security and intelligence
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Shield, label: "Intelligence" },
              { icon: Rocket, label: "Aerospace" },
              { icon: Target, label: "Cybersecurity" },
              { icon: Building2, label: "Defense" }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group bg-gray-900/40 border border-gray-800/50 rounded-lg p-6 text-center hover:border-gray-700/50 hover:bg-gray-900/60 transition-all duration-300"
              >
                <item.icon className="w-8 h-8 text-gray-600 group-hover:text-gray-400 mx-auto mb-3 transition-colors" strokeWidth={1.5} />
                <p className="text-gray-400 text-sm">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="relative py-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 
                className="text-3xl font-light text-white mb-6 tracking-wide"
                style={{ fontFamily: 'Georgia, Cambria, serif' }}
              >
                Who Can Apply
              </h2>
              <p className="text-gray-400 mb-8">
                The Unity Scholars program is being designed specifically for young people who have experienced foster care and demonstrate the drive to excel in STEM fields and national security careers.
              </p>

              <ul className="space-y-4">
                {[
                  "Current or former foster youth (ages 16-19)",
                  "Rising high school juniors or seniors",
                  "Strong interest in STEM, cybersecurity, or AI",
                  "U.S. citizenship (required for security clearance)",
                  "Commitment to the full program duration",
                  "Willingness to pursue federal career pathways"
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-gray-700/20 to-gray-800/20 rounded-2xl blur-xl" />
              <div className="relative bg-gray-900/80 border border-gray-800/50 rounded-2xl p-8">
                <h3 className="text-xl font-medium text-white mb-6">Planned Benefits</h3>
                <ul className="space-y-4">
                  {[
                    { icon: Briefcase, text: "Paid work-study position" },
                    { icon: GraduationCap, text: "College application & scholarship support" },
                    { icon: Users, text: "Dedicated mentor with federal background" },
                    { icon: Shield, text: "Security clearance preparation" },
                    { icon: BookOpen, text: "Professional development curriculum" },
                    { icon: Heart, text: "Wraparound support services" }
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-gray-800/50 border border-gray-700/50 flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                      </div>
                      <span className="text-gray-300">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-b from-gray-950 to-black overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gray-800/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Star className="w-12 h-12 text-gray-500 mx-auto mb-6" strokeWidth={1.5} />
            
            <h2 
              className="text-3xl md:text-4xl font-light text-white mb-6 tracking-wide"
              style={{ fontFamily: 'Georgia, Cambria, serif' }}
            >
              Your Future Starts Here
            </h2>
            
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              The Unity Scholars program is currently in development. Express your interest to be notified when applications open, or reach out if you'd like to support this initiative.
            </p>

            <div className="flex justify-center mb-12">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-black/30 border border-gray-600/30"
              >
                Express Interest
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <p className="text-gray-600 text-sm">
              Reticuli Technologies, LLC • 351 St Francis St, Mobile, AL 36602
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UnityScholarsProgram;